'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'data-fetching-client';
import { NavMarker } from 'react-rhumb';
import { useLocation } from 'react-router-dom';
import { parse as parseParams } from 'hub-http/helpers/params';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import H1 from 'UIComponents/elements/headings/H1';
import CanonicalLink from 'marketplace-ui-common/components/elements/CanonicalLink';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import { PublicOnly } from '../selectors/app';
import { getFullPublicThemesUrl } from '../utils/urls';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import ThemeBusinessTypesGrid from '../components/storefront/ThemeBusinessTypesGrid';
import { useCanonicalUrl } from '../common/hooks/useCanonicalUrl';
import { useSocialScreenshotUrl } from '../hooks/useSocialScreenshotUrl';
import { GET_UNUSED_FILTERS } from '../queries/unusedFiltersQuery';
const DEFAULT_EVENT_PROPERTIES = {
  screen: 'themes all business types',
  type: 'theme'
};

const ThemesAllBusinessTypesContainer = () => {
  const lang = getLang();
  const {
    search
  } = useLocation();
  const {
    showAll: showAllParam
  } = parseParams(search.slice(1));
  const [pageviewReady, setPageviewReady] = useState(false);
  const {
    loading: unusedFiltersIsLoading
  } = useQuery(GET_UNUSED_FILTERS);
  const {
    trackInteraction,
    trackPageview,
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  const canonicalUrl = useCanonicalUrl();
  const defaultMetaDescription = unescapedText('marketplace-assets-ui.themes.allBusinessTypes.metaDescription');
  useEffect(() => {
    if (pageviewReady) {
      trackPageview();
    } else {
      setDefaultEventProperties(DEFAULT_EVENT_PROPERTIES);
      setPageviewReady(true);
    }
  }, [pageviewReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.title = I18n.text('marketplace-assets-ui.themes.allBusinessTypes.title');
  }, []);
  const socialScreenshotUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "THEMES_ALL_BUSINESS_TYPES_LOAD",
    children: [/*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(MetaDescription, {
          content: defaultMetaDescription
        }), /*#__PURE__*/_jsx(CanonicalLink, {
          href: `${getFullPublicThemesUrl(lang)}/categories`
        }), /*#__PURE__*/_jsx(PrerenderReady, {
          ready: !unusedFiltersIsLoading
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialScreenshotUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: canonicalUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: unescapedText('marketplace-assets-ui.themes.allBusinessTypes.title')
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: defaultMetaDescription
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialScreenshotUrl
        })]
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      children: /*#__PURE__*/_jsxs(MainSectionInner, {
        children: [/*#__PURE__*/_jsx(H1, {
          "aria-level": 1,
          className: "is--heading-2",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.allBusinessTypes.title"
          })
        }), /*#__PURE__*/_jsx(ThemeBusinessTypesGrid, {
          onTrackInteraction: trackInteraction,
          showAll: showAllParam === 'true'
        })]
      })
    })]
  });
};

export default ThemesAllBusinessTypesContainer;